import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuChevronLeft, LuChevronRight, LuCrosshair, LuRoute, LuLoader, LuMapPin, LuWand } from 'react-icons/lu';
import api from '../../services/api';
import { CONTENEDOR, PAGINA, PantallaHeader } from '../ui/Pantalla';
import { getTodayISO, formatDateISO } from '../../utils/dateUtils';
import MapaLeaflet, { linkRuta } from './MapaLeaflet';
import FichaVisitaSheet from '../dashboard/FichaVisitaSheet';
import TarjetaPunto from './TarjetaPunto';
import CercaPanel from './CercaPanel';
import RecorridosAgenda from './RecorridosAgenda';
import { colorTecnico } from '../../utils/estados';
import ListaPendientes from './ListaPendientes';
import SinUbicarSheet from './SinUbicarSheet';
import BuscadorMapa from './BuscadorMapa';
import ArmarDiaSheet from '../trabajos/ArmarDiaSheet';
import VolverDeposito from './VolverDeposito';
import FiltroClienteMapa from './FiltroClienteMapa';
import RepetirDiaSheet from './RepetirDiaSheet';
import { VISTAS, COLOR, puntosDeVista, recorridosAgenda, leyenda, cercanos, conFoco, kmEntre } from './vistasMapa';

// Mapa del admin. 12-oct-2026: una vista por pregunta, un filtro por cliente y "¿Qué hay cerca?" para
// sumar trabajos al mismo viaje. 25-oct-2026: quedan dos vistas, Día y Pendientes (pedidos + service
// vencido juntos, con "Armar día"); Último service y Planificar repetían el Radar y "Armar día".
// Ver vistasMapa.js.
const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const sumarDias = (iso, n) => { const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() + n); return formatDateISO(d); };
const fmtDia = (iso, hoy) => {
    if (iso === hoy) return 'Hoy';
    if (iso === sumarDias(hoy, 1)) return 'Mañana';
    const [, m, d] = iso.split('-');
    return `${DIAS[new Date(iso + 'T12:00:00').getDay()]} ${d}/${m}`;
};
const control = 'h-9 rounded-xl bg-chip text-label font-black text-ink';

export default function MapaManager({ onCrearTrabajo, onAbrirPedido }) {
    const hoy = getTodayISO();
    const [fecha, setFecha] = useState(hoy);
    const [data, setData] = useState(null);
    // Se puede llegar desde Rentabilidad con una vista y un cliente ya elegidos
    const [pedidoInicial] = useState(() => { try { const v = JSON.parse(sessionStorage.getItem('mapaAbrir') || 'null'); sessionStorage.removeItem('mapaAbrir'); return v; } catch { return null; } });
    const [vista, setVista] = useState(pedidoInicial?.vista === 'equipos' ? 'equipos' : pedidoInicial?.vista && pedidoInicial.vista !== 'agenda' ? 'pendientes' : 'agenda');
    const [capas, setCapas] = useState({ pedidos: true, service: true });
    const [ocultos, setOcultos] = useState({});      // Equipos: colores apagados ({ nunca: true })
    const [zonaSel, setZonaSel] = useState(null);    // Equipos: ver una sola zona (CABA, SUR, OTRA)
    const [extra, setExtra] = useState(null);        // lugar buscado que no está en las capas
    const [armarDia, setArmarDia] = useState(false);
    const [tecnicosTodos, setTecnicosTodos] = useState([]);
    const [filtro, setFiltro] = useState(pedidoInicial?.filtro || 'todos');
    const [tecnico, setTecnico] = useState(null);
    const [sel, setSel] = useState(null);
    const [foco, setFoco] = useState(null);       // punto central de "¿Qué hay cerca?"
    const [radio, setRadio] = useState(2);
    const [corrigiendo, setCorrigiendo] = useState(null);
    const [verSinUbicar, setVerSinUbicar] = useState(false);
    const [ficha, setFicha] = useState(null);
    const [ajustar, setAjustar] = useState(0);
    const [acercar, setAcercar] = useState(false);   // el buscador pide acercarse al elegido
    const pedidoActual = useRef(0);

    useEffect(() => { setTecnico(null); setSel(null); }, [fecha]);

    const cargar = useCallback(async () => {
        const n = ++pedidoActual.current;
        try {
            const r = await api.get('/mapa', { params: { fecha } });
            if (n !== pedidoActual.current) return;
            setData(r.data);
            setAjustar(a => a + 1);
        } catch { toast.error('No se pudo cargar el mapa'); }
    }, [fecha]);
    useEffect(() => { cargar(); }, [cargar]);
    useEffect(() => { api.get('/ordenes/tecnicos').then(r => setTecnicosTodos(r.data || [])).catch(() => {}); }, []);
    // Punto de salida del día (29-oct-2026): cada técnico de su sede (Lucas del depósito, los demás de la base),
    // o el que se elija arriba para ver el recorrido desde otro lado
    const [sedes, setSedes] = useState([]);
    const [salidaSel, setSalidaSel] = useState('');   // '' = cada uno de la suya
    const [repetir, setRepetir] = useState(false);   // "Por dónde fue" (29-oct-2026)
    useEffect(() => {
        api.get('/rentabilidad/config').then(r => {
            const c = r.data || {};
            const l = [];
            if (c.deposito?.lat != null) l.push({ id: 'DEPOSITO', nombre: c.deposito.nombre || 'Depósito', lat: c.deposito.lat, lng: c.deposito.lng });
            if (c.base?.lat != null) l.push({ id: 'BASE', nombre: c.base.nombre || 'Base', lat: c.base.lat, lng: c.base.lng });
            (c.salidasGuardadas || []).forEach((x, i) => x?.lat != null && l.push({ id: `G${i}`, nombre: x.nombre || x.direccion, lat: x.lat, lng: x.lng }));
            setSedes(l);
        }).catch(() => {});
    }, []);
    const salidaDe = useCallback((v) => {
        const id = salidaSel || (/lucas/i.test(String(v?.tecnico || '')) ? 'DEPOSITO' : 'BASE');
        const x = sedes.find(y => y.id === id) || sedes.find(y => y.id === 'BASE');
        return x ? { id: `salida-${x.id}`, lat: x.lat, lng: x.lng, color: '#111827', etiqueta: 'S', titulo: `Salida · ${x.nombre}`, tituloFijo: true } : null;
    }, [sedes, salidaSel]);

    // Mientras haya direcciones buscándose, se refresca solo (sin mover el encuadre)
    useEffect(() => {
        if (!data?.pendientes) return undefined;
        const id = setTimeout(async () => {
            const n = pedidoActual.current;
            try { const r = await api.get('/mapa', { params: { fecha } }); if (n === pedidoActual.current) setData(r.data); } catch { /* */ }
        }, 20000);
        return () => clearTimeout(id);
    }, [data, fecha]);

    const puntos = useMemo(() => puntosDeVista(data, vista, { filtro, tecnico, capas, extra, ocultos })
        .filter(p => vista !== 'equipos' || !zonaSel || p.zona === zonaSel || p.id === extra), [data, vista, filtro, tecnico, capas, extra, ocultos, zonaSel]);
    // Equipos: los totales y las cantidades por color se cuentan con todos los colores (aunque estén apagados)
    const todosEquipos = useMemo(() => (vista === 'equipos' ? puntosDeVista(data, 'equipos', { filtro, extra: null }) : []), [data, vista, filtro]);
    const nEquipos = todosEquipos.reduce((a, p) => a + (p.equipos || 0), 0);
    const cerca = useMemo(() => (foco ? cercanos(data, foco, radio, filtro) : []), [data, foco, radio, filtro]);
    const masCerca = useMemo(() => (foco ? cercanos(data, foco, 1000, filtro)[0] || null : null), [data, foco, filtro]);
    // 2-nov-2026: en Equipos, cuántos lugares al día hay en el radio (se ven en el mapa pero no son para ofrecer;
    // antes decía "Nada en 1 km" con cuatro puntos verdes adentro)
    const alDiaCerca = useMemo(() => (foco && vista === 'equipos'
        ? todosEquipos.filter(p => p.id !== foco.id && p.lat != null && (p.semaforo === 'verde' || p.semaforo === 'nunca')
            && kmEntre(foco, p) <= radio).length : 0), [foco, vista, todosEquipos, radio]);
    // Con foco: lo de la vista fuera del radio se apaga, y se suma lo cercano de otras vistas
    const enMapa = useMemo(() => {
        if (!foco) {
            if (vista !== 'agenda') return puntos;
            const tecs = [...new Map(puntos.filter(p => p.lat != null && p.capa !== 'reservado').map(p => [p.tecnicoId, p])).values()];
            const inicios = [...new Map(tecs.map(salidaDe).filter(Boolean).map(x => [x.id, x])).values()];
            return [...inicios, ...puntos];
        }
        const ids = new Set(puntos.map(p => p.id));
        return [...conFoco(puntos, foco, radio), ...cerca.filter(x => !ids.has(x.id)).map(x => ({
            ...x, color: x.tipoCerca === 'pedido' ? (x.urgente ? '#991B1B' : '#EA580C') : (x.semaforo === 'rojo' ? '#DC2626' : '#D48800'),
        })), ...(ids.has(foco.id) ? [] : [{ ...foco, color: '#111827' }])];
    }, [puntos, foco, radio, cerca, vista, salidaDe]);

    const ubicados = puntos.filter(p => p.lat != null);
    const sinUbicar = puntos.filter(p => p.lat == null);
    const punto = enMapa.find(p => p.id === sel) || null;
    const ley = leyenda(vista, vista === 'equipos' ? todosEquipos : puntos);
    const lineas = vista === 'agenda' && !foco ? recorridosAgenda(puntos, salidaDe) : null;
    const circulos = foco ? [{ lat: foco.lat, lng: foco.lng, km: radio, color: '#C9341F' }] : null;

    const clientes = useMemo(() => Object.entries(data?.tiposCliente || {})
        .filter(([id, c]) => c.tipo === 'EMPRESA' || filtro === `c-${id}`).sort((a, b) => a[1].nombre.localeCompare(b[1].nombre)), [data, filtro]);
    const tecnicos = useMemo(() => {
        const m = new Map();
        (data?.visitas || []).forEach(v => { if (v.tecnicoId && !m.has(v.tecnicoId)) m.set(v.tecnicoId, v.tecnico); });
        return [...m.entries()].map(([id, nombre]) => ({ id, nombre }));
    }, [data]);

    const cambiarVista = (v) => { setVista(v); setZonaSel(null); setSel(null); setFoco(null); setTecnico(null); setExtra(null); setAcercar(false); setAjustar(a => a + 1); };
    const alternarCapa = (k) => { setCapas(c => ({ ...c, [k]: !c[k] })); setSel(null); setAjustar(a => a + 1); };
    const alternarColor = (k) => { setOcultos(o => ({ ...o, [k]: !o[k] })); setSel(null); setAjustar(a => a + 1); };
    const cambiarFiltro = (f) => { setFiltro(f); setSel(null); setAjustar(a => a + 1); };
    const abrirCerca = (p) => { setFoco(p); setSel(null); setAjustar(a => a + 1); };
    const elegirBuscado = (p, v) => {
        // Los lugares se muestran en Equipos (2-nov-2026: antes en Pendientes, aunque tuvieran el service al día)
        setVista(v === 'vender' ? 'equipos' : v); setExtra(v === 'vender' ? p.id : null); setFiltro('todos'); setTecnico(null); setFoco(null);
        if (p.lat == null) { setSel(null); setCorrigiendo(p); toast('No está ubicado: tocá el mapa donde queda', { icon: '📍' }); return; }
        setAcercar(true); setSel(p.id);
    };

    const fijarUbicacion = async (latlng) => {
        if (!corrigiendo) return;
        try {
            await api.put('/mapa/ubicacion', { direccion: corrigiendo.direccion, lat: latlng.lat, lng: latlng.lng });
            toast.success('Ubicación guardada');
            const id = corrigiendo.id;
            setCorrigiendo(null);
            await cargar();
            setSel(id);
        } catch { toast.error('No se pudo guardar'); }
    };
    const abrirVisita = async (ordenId) => {
        try { const r = await api.get(`/ordenes/${ordenId}`); setFicha(r.data); } catch { toast.error('No se pudo abrir la visita'); }
    };
    const ruta = tecnico && linkRuta(puntos.filter(v => v.tecnicoId === tecnico && !['COMPLETADA', 'NO_ATENDIDO', 'CANCELADA'].includes(v.estado)));
    const vacio = { agenda: 'No hay visitas ni reservas este día', pendientes: 'No hay pedidos ni service vencido', equipos: 'No hay equipos con dirección' }[vista];
    const nPedidos = data?.pedidos?.length || 0;
    const nService = puntosDeVista(data, 'pendientes', { filtro, capas: { pedidos: false }, extra: null }).length;

    return (
        <div className={PAGINA}>
            <div className={CONTENEDOR}>
                <PantallaHeader titulo="Mapa" subtitulo="El día de cada técnico, lo que queda por hacer y dónde están tus equipos" />

                <BuscadorMapa data={data} onElegir={elegirBuscado} />

                {/* Qué querés ver: una vista por vez */}
                <div className="grid grid-cols-3 gap-1 p-1 rounded-2xl bg-chip">
                    {VISTAS.map(v => (
                        <button key={v.id} type="button" onClick={() => cambiarVista(v.id)} aria-pressed={vista === v.id}
                            className={`h-11 px-1 rounded-xl text-label font-black leading-tight active:scale-95 transition-all ${vista === v.id ? 'bg-card text-ink shadow-sm' : 'text-secondary'}`}>
                            {v.label}
                        </button>
                    ))}
                </div>

                {/* Filtros: cliente y día/técnico (Día) o capas y Armar día (Pendientes) */}
                <div className="flex items-center gap-2 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
                    {vista === 'agenda' && (
                        <div className="flex items-center shrink-0 rounded-xl bg-chip">
                            <button type="button" onClick={() => setFecha(f => sumarDias(f, -1))} aria-label="Día anterior" className="w-9 h-9 flex items-center justify-center text-secondary"><LuChevronLeft size={17} /></button>
                            <button type="button" onClick={() => setFecha(hoy)} className="px-1 text-label font-black text-ink capitalize whitespace-nowrap">{fmtDia(fecha, hoy)}</button>
                            <button type="button" onClick={() => setFecha(f => sumarDias(f, 1))} aria-label="Día siguiente" className="w-9 h-9 flex items-center justify-center text-secondary"><LuChevronRight size={17} /></button>
                        </div>
                    )}
                    <FiltroClienteMapa filtro={filtro} empresas={clientes} onCambiar={cambiarFiltro} />
                    {vista === 'agenda' && tecnicos.length > 1 && tecnicos.map(t => {
                        const color = t.nombre ? colorTecnico(t.nombre) : '#9CA3AF';
                        return (
                            <button key={t.id} type="button" onClick={() => { setTecnico(x => (x === t.id ? null : t.id)); setAjustar(a => a + 1); }}
                                className={`${control} px-3 shrink-0 inline-flex items-center gap-1.5 border-2 ${tecnico === t.id ? 'bg-card' : 'border-transparent'}`}
                                style={tecnico === t.id ? { borderColor: color } : undefined}>
                                <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />{String(t.nombre || '').split(' ')[0]}
                            </button>
                        );
                    })}
                    {ruta && (
                        <a href={ruta} target="_blank" rel="noreferrer" className="h-9 px-3 shrink-0 rounded-xl inline-flex items-center gap-1.5 bg-[#C9341F] text-white text-label font-black">
                            <LuRoute size={15} /> Ruta
                        </a>
                    )}
                    {vista === 'agenda' && sedes.length > 1 && (
                        <select value={salidaSel} onChange={e => { setSalidaSel(e.target.value); setAjustar(a => a + 1); }} aria-label="Desde dónde sale"
                            className={`${control} px-2.5 shrink-0 outline-none`}>
                            <option value="">Sale cada uno de su sede</option>
                            {sedes.map(x => <option key={x.id} value={x.id}>Sale de {x.nombre}</option>)}
                        </select>
                    )}
                    {vista === 'agenda' && (data?.visitas || []).some(v => v.estado === 'COMPLETADA' && v.lat != null) && (
                        <button type="button" onClick={() => setRepetir(true)} className="h-9 px-3 shrink-0 rounded-xl inline-flex items-center gap-1.5 bg-chip text-ink text-label font-black active:scale-95">
                            <LuRoute size={15} /> Por dónde fue
                        </button>
                    )}
                    <VolverDeposito />
                </div>

                {/* Pendientes: qué capas ver y "Armar día", siempre a la vista */}
                {vista === 'pendientes' && (
                    <div className="flex items-center gap-2">
                        <div className="flex-1 min-w-0 flex gap-2 overflow-x-auto">
                            {[['pedidos', 'Pedidos', nPedidos, COLOR.pedido], ['service', 'Service vencido', nService, COLOR.vencido]].map(([k, label, n, color]) => (
                                <button key={k} type="button" onClick={() => alternarCapa(k)} aria-pressed={capas[k]}
                                    className={`${control} px-3 shrink-0 inline-flex items-center gap-1.5 border-2 ${capas[k] ? 'bg-card' : 'border-transparent opacity-60'}`}
                                    style={capas[k] ? { borderColor: color } : undefined}>
                                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />{label}<span className="text-muted">{n}</span>
                                </button>
                            ))}
                        </div>
                        <button type="button" onClick={() => setArmarDia(true)} className="h-9 px-3 shrink-0 rounded-xl inline-flex items-center gap-1.5 bg-brand-red text-white text-label font-black active:scale-95">
                            <LuWand size={15} /> Armar día
                        </button>
                    </div>
                )}

                {/* Equipos: el total y un botón por color para ocultarlo o mostrarlo (2-nov-2026) */}
                {vista === 'equipos' && data && (
                    <div className="p-3 rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.08] space-y-2.5">
                        <p className="text-body text-secondary"><b className="text-body-lg font-black text-ink">{todosEquipos.length} lugar{todosEquipos.length !== 1 ? 'es' : ''}</b> con {nEquipos} equipo{nEquipos !== 1 ? 's' : ''} · los números son lugares</p>
                        <div className="flex flex-wrap gap-2">
                            {ley.map(l => (
                                <button key={l.id} type="button" onClick={() => alternarColor(l.id)} aria-pressed={!ocultos[l.id]}
                                    className={`${control} px-3 inline-flex items-center gap-1.5 border-2 ${ocultos[l.id] ? 'border-transparent opacity-50' : 'bg-card'}`}
                                    style={ocultos[l.id] ? undefined : { borderColor: l.color }}>
                                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: l.color }} />{l.label}<span className="text-muted">{l.cantidad}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Mapa (isolate: sus capas no tapan el menú ni la campanita) */}
                <div className="relative isolate rounded-2xl overflow-hidden border border-black/[0.06] dark:border-white/[0.06]">
                    <MapaLeaflet puntos={enMapa} seleccionado={sel} ajustarKey={ajustar} lineas={lineas} circulos={circulos} acercar={acercar}
                        agrupar={vista === 'equipos' && !foco}
                        onClickPunto={(p) => { if (!corrigiendo && !p.apagado && !String(p.id).startsWith('salida')) { setAcercar(false); setSel(p.id); } }}
                        onClickMapa={(ll) => { if (corrigiendo) fijarUbicacion(ll); else setSel(null); }}
                        className={`w-full ${vista === 'pendientes' && !foco ? 'h-[50vh] min-h-[300px]' : 'h-[calc(var(--vh,1vh)*100-360px)] min-h-[340px]'} md:h-[calc(var(--vh,1vh)*100-300px)] ${corrigiendo ? 'cursor-crosshair' : ''}`} />

                    {/* Avisos abajo a la izquierda (arriba queda el zoom) */}
                    {!punto && !corrigiendo && (data?.pendientes > 0 || (data && ubicados.length === 0)) && (
                        <div className="absolute left-3 bottom-8 z-[500] pointer-events-none">
                            <span className="h-8 px-3 rounded-full bg-card shadow text-caption font-bold text-secondary inline-flex items-center gap-1.5">
                                {data.pendientes > 0 ? <><LuLoader size={13} className="animate-spin" /> Ubicando {data.pendientes} dirección{data.pendientes !== 1 ? 'es' : ''}…</> : vacio}
                            </span>
                        </div>
                    )}

                    {corrigiendo && (
                        <div className="absolute top-3 left-14 right-3 z-[600] p-3 rounded-2xl bg-card shadow-xl flex items-center gap-3">
                            <LuCrosshair size={20} className="text-brand-red shrink-0" />
                            <div className="flex-1 min-w-0">
                                <p className="text-body font-black text-ink truncate">Tocá en el mapa dónde queda</p>
                                <p className="text-caption text-muted truncate">{corrigiendo.cliente} · {corrigiendo.direccion}</p>
                            </div>
                            <button type="button" onClick={() => setCorrigiendo(null)} className="h-9 px-3 rounded-xl bg-chip text-label font-black text-secondary">Cancelar</button>
                        </div>
                    )}

                    {punto && !corrigiendo && (
                        <TarjetaPunto punto={punto} vista={vista} enFoco={foco?.id === punto.id}
                            onCerrar={() => setSel(null)} onCerca={() => abrirCerca(punto)} onVerVisita={abrirVisita}
                            onAbrirPedido={onAbrirPedido} onCrearTrabajo={onCrearTrabajo} onCorregir={() => setCorrigiendo(punto)} />
                    )}
                </div>

                {/* Leyenda con cantidades (y los que no están en el mapa) */}
                {data && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                        {vista !== 'equipos' && ley.map(l => (
                            <span key={l.id} className="inline-flex items-center gap-1.5 text-caption text-secondary">
                                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={l.hueco ? { border: `2px solid ${l.color}`, background: '#fff' } : { background: l.color }} />
                                <b className="text-ink">{l.cantidad}</b> {l.corto || l.label}
                            </span>
                        ))}
                        {sinUbicar.length > 0 && (
                            <button type="button" onClick={() => setVerSinUbicar(true)} className="inline-flex items-center gap-1 text-caption font-black text-brand-red">
                                <LuMapPin size={13} /> {sinUbicar.length} sin ubicar
                            </button>
                        )}
                        {!foco && vista !== 'agenda' && ubicados.length > 0 && <span className="text-caption text-muted w-full md:w-auto">Tocá un punto y "¿Qué hay cerca?" para sumar trabajos al viaje.</span>}
                    </div>
                )}

                {/* Agenda: el recorrido de cada técnico como lista, para abrirlo en Google Maps o compartirlo */}
                {vista === 'agenda' && !foco && (
                    <RecorridosAgenda puntos={puntos.filter(p => p.capa !== 'reservado')} fecha={fecha} onCambio={cargar} onVerVisita={abrirVisita}
                        onElegir={p => { setSel(p.id); setAcercar(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
                )}

                {/* Equipos: por zona (2-nov-2026). Tocar una zona muestra solo esa; otra vez, todas */}
                {vista === 'equipos' && !foco && todosEquipos.length > 0 && (
                    <div className="rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.08] divide-y divide-black/[0.06] dark:divide-white/[0.06]">
                        <p className="px-3.5 pt-3 pb-2 text-label font-black uppercase tracking-widest text-muted">Por zona</p>
                        {[['CABA', 'CABA'], ['SUR', 'Zona sur'], ['OTRA', 'Otras zonas']].map(([id, nombre]) => {
                            const z = todosEquipos.filter(p => p.zona === id);
                            if (!z.length) return null;
                            const n = (s) => z.filter(p => p.semaforo === s).length;
                            const activo = zonaSel === id;
                            return (
                                <button key={id} type="button" onClick={() => { setZonaSel(activo ? null : id); setSel(null); setAjustar(a => a + 1); }} aria-pressed={activo}
                                    className={`w-full px-3.5 py-2.5 flex items-center gap-3 text-left active:bg-chip ${activo ? 'bg-[rgba(232,66,47,0.08)]' : ''}`}>
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-body font-black text-ink">{nombre} <span className="text-muted font-bold">· {z.length} lugar{z.length !== 1 ? 'es' : ''} · {z.reduce((t, p) => t + (p.equipos || 0), 0)} equipos</span></span>
                                        <span className="flex flex-wrap gap-x-3 text-caption font-bold">
                                            {n('rojo') > 0 && <span className="text-[#DC2626]">{n('rojo')} vencido{n('rojo') !== 1 ? 's' : ''}</span>}
                                            {n('amarillo') > 0 && <span className="text-[#D48800]">{n('amarillo')} por vencer</span>}
                                            {n('nunca') > 0 && <span className="text-muted">{n('nunca')} sin service</span>}
                                            {!n('rojo') && !n('amarillo') && <span className="text-[#16A34A]">Todo al día</span>}
                                        </span>
                                    </span>
                                    <span className="shrink-0 text-label font-black text-brand-red">{activo ? 'Ver todas' : 'Ver en el mapa'}</span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Pendientes: todo en una lista por zona, para decidir sin tocar punto por punto (26-oct-2026) */}
                {vista === 'pendientes' && !foco && (
                    <ListaPendientes puntos={puntos} onElegir={p => {
                        if (p.lat == null) { setCorrigiendo(p); return; }
                        setSel(p.id); setAcercar(true); window.scrollTo({ top: 0, behavior: 'smooth' });
                    }} />
                )}

                {foco && (
                    <CercaPanel centro={foco} radio={radio} onRadio={r => { setRadio(r); setAjustar(a => a + 1); }} lista={cerca} masCerca={masCerca} alDia={alDiaCerca}
                        onCerrar={() => { setFoco(null); setAjustar(a => a + 1); }} onElegir={x => setSel(x.id)}
                        onAbrirPedido={onAbrirPedido} onCrearTrabajo={onCrearTrabajo} />
                )}
            </div>

            {repetir && <RepetirDiaSheet visitas={(data?.visitas || []).filter(v => v.estado === 'COMPLETADA')} salidaDe={salidaDe} fechaTxt={fmtDia(fecha, hoy)} onCerrar={() => setRepetir(false)} />}
            {armarDia && <ArmarDiaSheet tecnicos={tecnicosTodos} onCerrar={() => setArmarDia(false)} onListo={() => { setArmarDia(false); cargar(); }} />}
            {verSinUbicar && <SinUbicarSheet lista={sinUbicar} onCerrar={() => setVerSinUbicar(false)} onElegir={p => { setVerSinUbicar(false); setCorrigiendo(p); }} />}
            {ficha && <FichaVisitaSheet key={ficha.id} orden={ficha} onCerrar={() => setFicha(null)} onVerTrabajos={() => setFicha(null)} onEliminada={() => { setFicha(null); cargar(); }} />}
        </div>
    );
}
